import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { Mail, Building, ChevronDown, ChevronRight, UserPlus, Trash2, Users, Pencil } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout.jsx';
import CRUDTable from '../../components/crud/CRUDTable';
import CRUDModal from '../../components/crud/CRUDModal';
import DeleteConfirmModal from '../../components/crud/DeleteConfirmModal';

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// GraphQL Queries y Mutations
const GET_SUPPLIERS = gql`
  query GetSuppliers {
    suppliers {
      id
      supplier_name
      email
      notified {
        id
        email
        name
      }
    }
  }
`;

const CREATE_SUPPLIER = gql`
  mutation CreateSupplier($supplier_name: String!, $email: String) {
    createSupplier(supplier_name: $supplier_name, email: $email) {
      id
      supplier_name
      email
      notified {
        id
        email
        name
      }
    }
  }
`;

const UPDATE_SUPPLIER = gql`
  mutation UpdateSupplier($id: UUID!, $supplier_name: String!, $email: String) {
    updateSupplier(id: $id, supplier_name: $supplier_name, email: $email) {
      id
      supplier_name
      email
      notified {
        id
        email
        name
      }
    }
  }
`;

const DELETE_SUPPLIER = gql`
  mutation DeleteSupplier($id: UUID!) {
    deleteSupplier(id: $id)
  }
`;

const ADD_SUPPLIER_NOTIFIED = gql`
  mutation AddSupplierNotified($supplierId: UUID!, $email: String!, $name: String) {
    addSupplierNotified(supplierId: $supplierId, email: $email, name: $name) {
      id
      supplierId
      email
      name
    }
  }
`;

const UPDATE_SUPPLIER_NOTIFIED = gql`
  mutation UpdateSupplierNotified($id: UUID!, $email: String!, $name: String) {
    updateSupplierNotified(id: $id, email: $email, name: $name) {
      id
      supplierId
      email
      name
    }
  }
`;

const REMOVE_SUPPLIER_NOTIFIED = gql`
  mutation RemoveSupplierNotified($id: UUID!) {
    removeSupplierNotified(id: $id)
  }
`;

// Subcomponente para gestionar los notificados de un proveedor
const SupplierNotifiedPanel = ({ supplier }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editEmail, setEditEmail] = useState('');
  const [editName, setEditName] = useState('');

  const [addNotified, { loading: addLoading }] = useMutation(ADD_SUPPLIER_NOTIFIED, {
    onCompleted: () => {
      toast.success('Notificado agregado');
      setNewEmail('');
      setNewName('');
      setIsAdding(false);
    },
    onError: (error) => toast.error(error.message),
    refetchQueries: [{ query: GET_SUPPLIERS }],
  });

  const [updateNotified, { loading: updateLoading }] = useMutation(UPDATE_SUPPLIER_NOTIFIED, {
    onCompleted: () => {
      toast.success('Notificado actualizado');
      setEditingId(null);
    },
    onError: (error) => toast.error(error.message),
    refetchQueries: [{ query: GET_SUPPLIERS }],
  });

  const [removeNotified, { loading: removeLoading }] = useMutation(REMOVE_SUPPLIER_NOTIFIED, {
    onCompleted: () => toast.success('Notificado eliminado'),
    onError: (error) => toast.error(error.message),
    refetchQueries: [{ query: GET_SUPPLIERS }],
  });

  const handleAdd = () => {
    const trimmedEmail = newEmail.trim();
    if (!trimmedEmail) {
      toast.error('El correo es obligatorio');
      return;
    }
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      toast.error('El formato del correo electrónico no es válido');
      return;
    }
    addNotified({
      variables: {
        supplierId: supplier.id,
        email: trimmedEmail,
        name: newName.trim() || null,
      },
    });
  };

  const handleStartEdit = (n) => {
    setEditingId(n.id);
    setEditEmail(n.email);
    setEditName(n.name || '');
    setIsAdding(false);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditEmail('');
    setEditName('');
  };

  const handleSaveEdit = () => {
    const trimmedEmail = editEmail.trim();
    if (!trimmedEmail) {
      toast.error('El correo es obligatorio');
      return;
    }
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      toast.error('El formato del correo electrónico no es válido');
      return;
    }
    updateNotified({
      variables: {
        id: editingId,
        email: trimmedEmail,
        name: editName.trim() || null,
      },
    });
  };

  const notifiedCount = supplier.notified?.length || 0;

  return (
    <div className="mt-1">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
      >
        {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <Users className="w-3.5 h-3.5" />
        <span>{notifiedCount} notificado{notifiedCount !== 1 ? 's' : ''}</span>
      </button>

      {isExpanded && (
        <div className="mt-2 ml-2 border-l-2 border-indigo-100 pl-3 space-y-2">
          {/* Lista de notificados */}
          {supplier.notified && supplier.notified.length > 0 ? (
            supplier.notified.map((n) =>
              editingId === n.id ? (
                // Formulario de edición inline
                <div key={n.id} className="space-y-1.5 py-1">
                  <input
                    type="text"
                    placeholder="Nombre (opcional)"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full text-xs border border-indigo-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                  />
                  <input
                    type="email"
                    placeholder="Correo electrónico *"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
                    className="w-full text-xs border border-indigo-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                  />
                  <div className="flex gap-1.5">
                    <button
                      onClick={handleSaveEdit}
                      disabled={updateLoading}
                      className="text-xs bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                    >
                      {updateLoading ? 'Guardando...' : 'Guardar'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded border border-gray-200 transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                // Fila de lectura normal
                <div key={n.id} className="flex items-center justify-between gap-2 py-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <Mail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      {n.name && (
                        <p className="text-xs font-medium text-gray-700 truncate">{n.name}</p>
                      )}
                      <p className="text-xs text-gray-500 truncate">{n.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleStartEdit(n)}
                      className="text-indigo-400 hover:text-indigo-600 transition-colors"
                      title="Editar notificado"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => removeNotified({ variables: { id: n.id } })}
                      disabled={removeLoading}
                      className="text-red-400 hover:text-red-600 transition-colors"
                      title="Eliminar notificado"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )
            )
          ) : (
            <p className="text-xs text-gray-400 italic">Sin notificados</p>
          )}

          {/* Formulario para agregar */}
          {isAdding ? (
            <div className="space-y-1.5 pt-1">
              <input
                type="text"
                placeholder="Nombre (opcional)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              />
              <input
                type="email"
                placeholder="Correo electrónico *"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              />
              <div className="flex gap-1.5">
                <button
                  onClick={handleAdd}
                  disabled={addLoading}
                  className="text-xs bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {addLoading ? 'Guardando...' : 'Guardar'}
                </button>
                <button
                  onClick={() => { setIsAdding(false); setNewEmail(''); setNewName(''); }}
                  className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded border border-gray-200 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            !editingId && (
              <button
                onClick={() => setIsAdding(true)}
                className="flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 mt-1 transition-colors"
              >
                <UserPlus className="w-3.5 h-3.5" />
                <span>Agregar notificado</span>
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
};

const SuppliersPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Queries
  const { data, loading, error } = useQuery(GET_SUPPLIERS);

  // Mutations
  const [createSupplier, { loading: createLoading }] = useMutation(CREATE_SUPPLIER, {
    onCompleted: () => {
      toast.success('Proveedor creado exitosamente');
      setIsModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
    refetchQueries: [{ query: GET_SUPPLIERS }]
  });

  const [updateSupplier, { loading: updateLoading }] = useMutation(UPDATE_SUPPLIER, {
    onCompleted: () => {
      toast.success('Proveedor actualizado exitosamente');
      setIsModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
    refetchQueries: [{ query: GET_SUPPLIERS }]
  });

  const [deleteSupplier, { loading: deleteLoading }] = useMutation(DELETE_SUPPLIER, {
    onCompleted: () => {
      toast.success('Proveedor eliminado exitosamente');
      setIsDeleteModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
    refetchQueries: [{ query: GET_SUPPLIERS }]
  });

  // Configuración de columnas para la tabla
  const columns = [
    {
      key: 'supplier_name',
      label: 'Nombre del Proveedor',
      sortable: true,
      render: (name, row) => (
        <div>
          <div className="flex items-center">
            <Building className="w-4 h-4 text-gray-500 mr-2" />
            <span className="font-medium">{name}</span>
          </div>
          <SupplierNotifiedPanel supplier={row} />
        </div>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      type: 'email',
      render: (email) => (
        email ? (
          <div className="flex items-center">
            <Mail className="w-4 h-4 text-gray-500 mr-2" />
            <a href={`mailto:${email}`} className="text-blue-600 hover:text-blue-800">
              {email}
            </a>
          </div>
        ) : (
          <span className="text-gray-500">Sin email</span>
        )
      ),
    },
  ];

  // Configuración de campos para el modal
  const formFields = [
    {
      name: 'supplier_name',
      label: 'Nombre del Proveedor',
      type: 'text',
      required: true,
      placeholder: 'Ingrese el nombre del proveedor',
      minLength: 2,
      maxLength: 100,
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: false,
      placeholder: 'Ingrese el email (opcional)',
      maxLength: 255,
    },
  ];

  // Handlers
  const handleAdd = () => {
    setSelectedSupplier(null);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleEdit = (supplier) => {
    setSelectedSupplier(supplier);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDelete = (supplier) => {
    setSelectedSupplier(supplier);
    setIsDeleteModalOpen(true);
  };

  const handleSave = async (formData) => {
    try {
      if (isEditing && selectedSupplier) {
        await updateSupplier({
          variables: {
            id: selectedSupplier.id,
            supplier_name: formData.supplier_name,
            email: formData.email || null,
          },
        });
      } else {
        await createSupplier({
          variables: {
            supplier_name: formData.supplier_name,
            email: formData.email || null,
          },
        });
      }
    } catch (error) {
      // Error handled by mutation onError
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteSupplier({
        variables: {
          id: selectedSupplier.id,
        },
      });
    } catch (error) {
      // Error handled by mutation onError
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSupplier(null);
    setIsEditing(false);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedSupplier(null);
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error al cargar los proveedores: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Gestión de Proveedores
          </h1>
          <p className="text-gray-600">
            Administra los proveedores y sus contactos notificados para los envíos del sistema
          </p>
        </div>

        <CRUDTable
          data={data?.suppliers || []}
          columns={columns}
          isLoading={loading}
          searchPlaceholder="Buscar proveedores..."
          searchFields={['supplier_name', 'email']}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          addButtonText="Agregar Proveedor"
          emptyMessage="No hay proveedores disponibles"
        />

        <CRUDModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSave}
          title={isEditing ? 'Editar Proveedor' : 'Agregar Proveedor'}
          initialData={selectedSupplier || {}}
          fields={formFields}
          isLoading={createLoading || updateLoading}
          isEditing={isEditing}
        />

        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          title="Eliminar Proveedor"
          message="¿Estás seguro de que deseas eliminar este proveedor?"
          itemName={selectedSupplier?.supplier_name}
          isLoading={deleteLoading}
        />
      </div>
    </DashboardLayout>
  );
};

export default SuppliersPage;
